import type { NodeTelemetrySeriesKey, NodeTelemetryState } from "@k8s-dashboard/contracts";

/**
 * 노드 화면의 표시 문구 결합 (#207)
 * --------------------------------------------------------------------------
 * 서버는 **닫힌 코드만** 보냅니다(시리즈 키·상태·사유 코드). 문구는 전부 여기서
 * i18n id로 바뀝니다 — 서버가 문장을 보내면 그 문장이 서버의 언어로 고정되어
 * en 모드에서도 한국어가 나옵니다.
 *
 * 표를 **리터럴로** 적는 이유: 템플릿 보간(`...reason.${code}`)으로 만들면 i18n
 * 게이트가 참조를 찾지 못해 살아 있는 id를 "미사용"으로 잡고, 반대로 id를 지웠을 때
 * 깨지는 것도 못 잡습니다. 닫힌 집합이므로 표 하나로 적는 것이 맞습니다.
 */

export const TELEMETRY_STATE_LABEL: Record<NodeTelemetryState, string> = {
  ok: "common.node.telemetry.state.ok",
  warming: "common.node.telemetry.state.warming",
  missing: "common.node.telemetry.state.missing",
  degraded: "common.node.telemetry.state.degraded",
};

export const SERIES_LABEL: Record<NodeTelemetrySeriesKey, string> = {
  "cpu.used": "common.node.series.cpu.used",
  "memory.used": "common.node.series.memory.used",
  "memory.total": "common.node.series.memory.total",
};

/**
 * 사유 코드 → 문구. 서버 계약의 `NodeTelemetryReason`과 **같은 집합**이며,
 * node-parity 테스트가 Go·TS 양쪽을 고정하므로 코드가 늘면 여기도 늘어야 합니다.
 */
export const TELEMETRY_REASON_LABEL = {
  warming_rate_window: "common.node.telemetry.reason.warming_rate_window",
  warming_no_post_birth_sample: "common.node.telemetry.reason.warming_no_post_birth_sample",
  series_absent: "common.node.telemetry.reason.series_absent",
  no_sample_in_range: "common.node.telemetry.reason.no_sample_in_range",
  no_common_evaluation: "common.node.telemetry.reason.no_common_evaluation",
  upstream_unavailable: "common.node.telemetry.reason.upstream_unavailable",
  upstream_invalid: "common.node.telemetry.reason.upstream_invalid",
  query_unsupported: "common.node.telemetry.reason.query_unsupported",
  identity_ambiguous: "common.node.telemetry.reason.identity_ambiguous",
  identity_mismatch: "common.node.telemetry.reason.identity_mismatch",
  pair_broken: "common.node.telemetry.reason.pair_broken",
  point_budget_exceeded: "common.node.telemetry.reason.point_budget_exceeded",
  cross_family_identity_mismatch: "common.node.telemetry.reason.cross_family_identity_mismatch",
  dependency_degraded: "common.node.telemetry.reason.dependency_degraded",
} as const satisfies Record<string, string>;

/**
 * 비율 표시 — 분모가 없으면 `null`이고, 100%를 **넘으면 그대로 보여 줍니다**.
 *
 * 노드의 request/allocatable이 1을 넘을 수 없다는 가정은 안전하지 않습니다:
 * allocatable은 kubelet 보고에 따라 줄어들 수 있고, 그 사이 스케줄된 Pod는 남아
 * 있으며 캐시된 상태가 과도기적으로 어긋날 수 있습니다. 넘는 값을 100%로 깎으면
 * 운영자가 "꽉 찼다"와 "초과 할당됐다"를 구분할 수 없습니다.
 */
export function ratioPercentText(ratio: number | null): string | null {
  if (ratio === null) return null;
  return `${(ratio * 100).toFixed(0)}%`;
}

export function ratioExceeds(ratio: number | null): boolean {
  return ratio !== null && ratio > 1;
}
